import React, { useState, useEffect, useMemo } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { FileText, Clock, Calculator, CheckCircle2, AlertTriangle, Download, Printer, RefreshCw } from 'lucide-react';
import { formatDate } from '@/lib/utils';
import * as XLSX from 'xlsx';
import { formatCurrency } from '@/utils/formatters';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function InvoiceReportContent() {
  const { getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState(null);
  const [activeTab, setActiveTab] = useState('invoices_pending');
  
  // Filters
  const [supplierFilter, setSupplierFilter] = useState('all');
  const [ageFilter, setAgeFilter] = useState('all');

  const fetchReportData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/store/invoice-report`, {
        headers: getAuthHeader()
      });
      setReportData(response.data);
    } catch (error) {
      console.error('Error fetching report:', error);
      toast.error('Failed to load invoice report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReportData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Get unique suppliers for filter
  const suppliers = useMemo(() => {
    if (!reportData) return [];
    const allEntries = [...(reportData.invoices_pending || []), ...(reportData.accounting_pending || [])];
    const uniqueSuppliers = [...new Set(allEntries.map(e => e.supplier_name).filter(Boolean))];
    return uniqueSuppliers.sort();
  }, [reportData]);

  // Filter entries based on selected filters
  const filteredInvoicesPending = useMemo(() => {
    const entries = reportData?.invoices_pending;
    if (!entries) return [];
    return entries.filter(entry => {
      if (supplierFilter !== 'all' && entry.supplier_name !== supplierFilter) return false;
      if (ageFilter !== 'all' && entry.aging_bucket !== ageFilter) return false;
      return true;
    });
  }, [reportData, supplierFilter, ageFilter]);

  const filteredAccountingPending = useMemo(() => {
    const entries = reportData?.accounting_pending;
    if (!entries) return [];
    return entries.filter(entry => {
      if (supplierFilter !== 'all' && entry.supplier_name !== supplierFilter) return false;
      if (ageFilter !== 'all' && entry.aging_bucket !== ageFilter) return false;
      return true;
    });
  }, [reportData, supplierFilter, ageFilter]);

  // Get aging badge
  const getAgingBadge = (entry) => {
    const colors = {
      'green': 'bg-green-100 text-green-700 border-green-200',
      'yellow': 'bg-yellow-100 text-yellow-700 border-yellow-200',
      'orange': 'bg-orange-100 text-orange-700 border-orange-200',
      'red': 'bg-red-100 text-red-700 border-red-200'
    };
    return (
      <Badge variant="outline" className={colors[entry.aging_color] || colors.green}>
        {entry.aging_bucket} days
      </Badge>
    );
  };

  // Export to Excel
  const handleExport = () => {
    const data = activeTab === 'invoices_pending' ? filteredInvoicesPending : filteredAccountingPending;
    
    const exportData = data.map(entry => ({
      'GRN No.': entry.store_receipt_no || '-',
      'Date': entry.created_at ? new Date(entry.created_at).toLocaleDateString('en-IN') : '-',
      'Supplier': entry.supplier_name || '-',
      'PO No.': entry.po_number || '-',
      'Amount (₹)': entry.total_supplier_amount || 0,
      'Age (Days)': entry.days_since_grn || 0,
      'Aging Bucket': entry.aging_bucket || '-',
      ...(activeTab === 'accounting_pending' ? {
        'Invoice Received': entry.invoice_received_at ? new Date(entry.invoice_received_at).toLocaleString('en-IN') : '-',
        'Received By': entry.invoice_received_by || '-'
      } : {})
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, activeTab === 'invoices_pending' ? 'Invoices Pending' : 'Accounting Pending');
    XLSX.writeFile(wb, `Invoice_Report_${activeTab}_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success('Report exported successfully');
  };

  // Print table
  const handlePrint = () => {
    const data = activeTab === 'invoices_pending' ? filteredInvoicesPending : filteredAccountingPending;
    const title = activeTab === 'invoices_pending' ? 'Invoices Pending Report' : 'Accounting Pending Report';
    
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${title}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          h1 { font-size: 18px; margin-bottom: 10px; }
          p { font-size: 12px; color: #666; margin-bottom: 15px; }
          table { width: 100%; border-collapse: collapse; font-size: 11px; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background-color: #f5f5f5; font-weight: bold; }
          .amount { text-align: right; }
          .center { text-align: center; }
          .aging-green { background-color: #dcfce7; }
          .aging-yellow { background-color: #fef9c3; }
          .aging-orange { background-color: #ffedd5; }
          .aging-red { background-color: #fee2e2; }
          @media print { body { margin: 0; } }
        </style>
      </head>
      <body>
        <h1>${title}</h1>
        <p>Generated on: ${new Date().toLocaleString('en-IN')} | Total Records: ${data.length}</p>
        <table>
          <thead>
            <tr>
              <th>GRN No.</th>
              <th>Date</th>
              <th>Supplier</th>
              <th>PO No.</th>
              <th class="amount">Amount (₹)</th>
              <th class="center">Age</th>
              <th class="center">Aging</th>
              ${activeTab === 'accounting_pending' ? '<th>Inv Received</th><th>Received By</th>' : ''}
            </tr>
          </thead>
          <tbody>
            ${data.map(entry => `
              <tr>
                <td>${entry.store_receipt_no || '-'}</td>
                <td>${entry.created_at ? new Date(entry.created_at).toLocaleDateString('en-IN') : '-'}</td>
                <td>${entry.supplier_name || '-'}</td>
                <td>${entry.po_number || '-'}</td>
                <td class="amount">${(entry.total_supplier_amount || 0).toLocaleString('en-IN')}</td>
                <td class="center">${entry.days_since_grn || 0} days</td>
                <td class="center aging-${entry.aging_color}">${entry.aging_bucket}</td>
                ${activeTab === 'accounting_pending' ? `
                  <td>${entry.invoice_received_at ? new Date(entry.invoice_received_at).toLocaleString('en-IN') : '-'}</td>
                  <td>${entry.invoice_received_by || '-'}</td>
                ` : ''}
              </tr>
            `).join('')}
          </tbody>
        </table>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  if (loading) {
    return (
        <div className="p-6 flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
    );
  }

  const summary = reportData?.summary || {};

  return (
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <FileText className="w-6 h-6" />
              Invoice Report
            </h1>
            <p className="text-muted-foreground">Track invoice receipt and accounting status</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleExport}>
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
            <Button variant="outline" size="sm" onClick={handlePrint}>
              <Printer className="w-4 h-4 mr-2" />
              Print
            </Button>
            <Button variant="outline" size="sm" onClick={fetchReportData}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-2 rounded-lg bg-gray-100">
                  <FileText className="w-5 h-5 text-gray-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total GRNs</p>
                  <p className="text-2xl font-bold">{summary.total_grns || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className={summary.invoices_pending_count > 0 ? 'border-red-200 bg-red-50' : ''}>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className={`p-2 rounded-lg ${summary.invoices_pending_count > 0 ? 'bg-red-100' : 'bg-gray-100'}`}>
                  <Clock className={`w-5 h-5 ${summary.invoices_pending_count > 0 ? 'text-red-600' : 'text-gray-600'}`} />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Invoices Pending</p>
                  <p className={`text-2xl font-bold ${summary.invoices_pending_count > 0 ? 'text-red-600' : ''}`}>
                    {summary.invoices_pending_count || 0}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {formatCurrency(summary.invoices_pending_amount || 0)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className={summary.accounting_pending_count > 0 ? 'border-orange-200 bg-orange-50' : ''}>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className={`p-2 rounded-lg ${summary.accounting_pending_count > 0 ? 'bg-orange-100' : 'bg-gray-100'}`}>
                  <Calculator className={`w-5 h-5 ${summary.accounting_pending_count > 0 ? 'text-orange-600' : 'text-gray-600'}`} />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Accounting Pending</p>
                  <p className={`text-2xl font-bold ${summary.accounting_pending_count > 0 ? 'text-orange-600' : ''}`}>
                    {summary.accounting_pending_count || 0}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {formatCurrency(summary.accounting_pending_amount || 0)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-2 rounded-lg bg-green-100">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Completed</p>
                  <p className="text-2xl font-bold text-green-600">
                    {(summary.total_grns || 0) - (summary.invoices_pending_count || 0) - (summary.accounting_pending_count || 0)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs and Table */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="flex justify-between items-center">
            <TabsList>
              <TabsTrigger value="invoices_pending" className="gap-2">
                <Clock className="w-4 h-4" />
                Invoices Pending ({reportData?.invoices_pending?.length || 0})
              </TabsTrigger>
              <TabsTrigger value="accounting_pending" className="gap-2">
                <Calculator className="w-4 h-4" />
                Accounting Pending ({reportData?.accounting_pending?.length || 0})
              </TabsTrigger>
            </TabsList>

            {/* Filters */}
            <div className="flex gap-2">
              <Select value={supplierFilter} onValueChange={setSupplierFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="All Suppliers" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Suppliers</SelectItem>
                  {suppliers.map(supplier => (
                    <SelectItem key={supplier} value={supplier}>{supplier}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={ageFilter} onValueChange={setAgeFilter}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="All Ages" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Ages</SelectItem>
                  <SelectItem value="0-7">
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-green-500"></span>
                      0-7 days
                    </span>
                  </SelectItem>
                  <SelectItem value="8-15">
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
                      8-15 days
                    </span>
                  </SelectItem>
                  <SelectItem value="16-30">
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-orange-500"></span>
                      16-30 days
                    </span>
                  </SelectItem>
                  <SelectItem value="30+">
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-red-500"></span>
                      30+ days
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Invoices Pending Tab */}
          <TabsContent value="invoices_pending" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle>Invoices Pending</CardTitle>
                <CardDescription>GRNs where physical invoice has not been received yet</CardDescription>
              </CardHeader>
              <CardContent>
                {filteredInvoicesPending.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <CheckCircle2 className="w-12 h-12 mx-auto mb-4 opacity-50 text-green-500" />
                    <p>No pending invoices!</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>GRN No.</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Supplier</TableHead>
                        <TableHead>PO No.</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead className="text-center">Age</TableHead>
                        <TableHead className="text-center">Aging</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredInvoicesPending.map(entry => (
                        <TableRow 
                          key={entry.id}
                          className={
                            entry.aging_color === 'red' ? 'bg-red-50' :
                            entry.aging_color === 'orange' ? 'bg-orange-50' :
                            entry.aging_color === 'yellow' ? 'bg-yellow-50' : ''
                          }
                        >
                          <TableCell className="font-mono font-medium">{entry.store_receipt_no}</TableCell>
                          <TableCell>{entry.created_at ? formatDate(entry.created_at) : '-'}</TableCell>
                          <TableCell>{entry.supplier_name || '-'}</TableCell>
                          <TableCell>{entry.po_number || '-'}</TableCell>
                          <TableCell className="text-right font-bold">
                            {formatCurrency(entry.total_supplier_amount || 0)}
                          </TableCell>
                          <TableCell className="text-center">{entry.days_since_grn} days</TableCell>
                          <TableCell className="text-center">{getAgingBadge(entry)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Accounting Pending Tab */}
          <TabsContent value="accounting_pending" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle>Accounting Pending</CardTitle>
                <CardDescription>Invoices received but accounting entry not yet done</CardDescription>
              </CardHeader>
              <CardContent>
                {filteredAccountingPending.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <CheckCircle2 className="w-12 h-12 mx-auto mb-4 opacity-50 text-green-500" />
                    <p>All invoices have been accounted!</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>GRN No.</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Supplier</TableHead>
                        <TableHead>PO No.</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead>Inv Received</TableHead>
                        <TableHead>Received By</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredAccountingPending.map(entry => (
                        <TableRow key={entry.id}>
                          <TableCell className="font-mono font-medium">{entry.store_receipt_no}</TableCell>
                          <TableCell>{entry.created_at ? formatDate(entry.created_at) : '-'}</TableCell>
                          <TableCell>{entry.supplier_name || '-'}</TableCell>
                          <TableCell>{entry.po_number || '-'}</TableCell>
                          <TableCell className="text-right font-bold">
                            {formatCurrency(entry.total_supplier_amount || 0)}
                          </TableCell>
                          <TableCell className="text-sm">
                            {entry.invoice_received_at ? (
                              <span className="text-green-600">
                                {new Date(entry.invoice_received_at).toLocaleDateString('en-IN')}
                              </span>
                            ) : '-'}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {entry.invoice_received_by || '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Supplier Summary */}
        {reportData?.supplier_summary?.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Supplier-wise Summary</CardTitle>
              <CardDescription>Pending invoices and accounting by supplier</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Supplier</TableHead>
                    <TableHead className="text-center">Invoice Pending</TableHead>
                    <TableHead className="text-right">Inv Pending Amt</TableHead>
                    <TableHead className="text-center">Acct Pending</TableHead>
                    <TableHead className="text-right">Acct Pending Amt</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reportData.supplier_summary.map((supplier, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="font-medium">{supplier.supplier_name}</TableCell>
                      <TableCell className="text-center">
                        {supplier.invoice_pending_count > 0 ? (
                          <Badge variant="destructive">{supplier.invoice_pending_count}</Badge>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {supplier.invoice_pending_amount > 0 
                          ? formatCurrency(supplier.invoice_pending_amount)
                          : '-'
                        }
                      </TableCell>
                      <TableCell className="text-center">
                        {supplier.accounting_pending_count > 0 ? (
                          <Badge variant="outline" className="bg-orange-100 text-orange-700 border-orange-200">
                            {supplier.accounting_pending_count}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {supplier.accounting_pending_amount > 0 
                          ? formatCurrency(supplier.accounting_pending_amount)
                          : '-'
                        }
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </div>
  );
}

// Standalone page wrapper
export default function InvoiceReportPage() {
  return (
    <DashboardLayout>
      <InvoiceReportContent />
    </DashboardLayout>
  );
}
